import React from 'react';
import { WidgetProps } from '@rjsf/core';
import { useIntl, FormattedMessage } from 'lib/intl';
import {
  TextField,
  HelperText,
} from 'components/form-elements/Forms/FieldEditText';
import { LocaleKey } from 'translations';

const MIN_VALUE = 0;
const MAX_VALUE = 100;
const MAX_DECIMALS = 1;

/*
 * Resolves an instant-validation translation key for the raw input string,
 * or `null` when the value is valid / empty. Works on the raw text (not the
 * parsed number) so we can detect "more than one decimal" before rounding.
 * */
function resolveErrorKey(raw: string): LocaleKey | null {
  if (!raw) return null;

  const value = Number(raw);
  if (Number.isNaN(value) || value < MIN_VALUE || value > MAX_VALUE) {
    return 'errors.uvHardeningSpeed.range';
  }

  const decimals = raw.split('.')[1];
  if (decimals && decimals.length > MAX_DECIMALS) {
    return 'errors.uvHardeningSpeed.decimal';
  }

  return null;
}

function valueToText(value: WidgetProps['value']): string {
  return value === 0 || value ? String(value) : '';
}

/*
 * Numeric input for the "UV Train - Speed" field.
 *
 * - Shows a persistent helper text describing the allowed input (0-100,
 *   one decimal), which switches to a red validation note the moment the
 *   user types an out-of-range value or a second decimal.
 * - Keeps a local text buffer so the user can type intermediate values
 *   such as "12." without the controlled number conversion dropping the dot.
 * */
export function UvHardeningSpeedWidget(props: WidgetProps) {
  const intl = useIntl();

  const labelId = (props.uiSchema['ui:title'] ?? props.label) as LocaleKey;

  const [text, setText] = React.useState<string>(valueToText(props.value));

  /*
   * Keep the local buffer in sync when the value is changed from outside
   * (e.g. switching between relining sections or a form reset), but avoid
   * clobbering intermediate input like "12." or "25.0" on our own round-trip.
   * */
  React.useEffect(() => {
    setText((current) => {
      if (Number(current) === props.value) return current;
      return valueToText(props.value);
    });
  }, [props.value]);

  const errorKey = resolveErrorKey(text);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    setText(raw);

    if (raw === '') {
      props.onChange(undefined);
      return;
    }

    const numeric = Number(raw);
    props.onChange(Number.isNaN(numeric) ? undefined : numeric);
  };

  const handleBlur = () => props.onBlur(props.id, props.value);
  const handleFocus = () => props.onFocus(props.id, props.value);

  return (
    <React.Fragment>
      <TextField
        id={props.id}
        name={props.id}
        type="number"
        label={intl.formatMessage({ id: labelId })}
        value={text}
        isError={Boolean(errorKey)}
        disabled={props.disabled || props.readonly}
        autoFocus={props.autofocus}
        placeholder={intl.formatMessage({
          id: 'relining.uvHardeningSpeed.placeholder',
        })}
        backgroundColor="outlinedTextWidget.background"
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
      />

      <HelperText isError={Boolean(errorKey)}>
        <FormattedMessage id={errorKey ?? 'relining.uvHardeningSpeed.hint'} />
      </HelperText>
    </React.Fragment>
  );
}

